import React from "react";
import { Link } from "react-router-dom";

const servicePillars = [
  {
    id: "real-estate",
    title: "Real Estate",
    image: "service-real-estate.jpg",
    imageAlt: "Traditional two-story home at dusk with warm exterior lighting",
    intro:
      "Buying, selling, and investment decisions backed by construction and planning insight — not just market data.",
    items: [
      "Residential buying and selling",
      "Investment property guidance",
      "Market analysis and pricing strategy",
      "Condominium expertise (CCS certified)",
      "Property evaluation with renovation potential in mind",
    ],
    closing:
      "Better decisions at the purchase stage reduce risk and unlock opportunity later.",
  },
  {
    id: "design-planning",
    title: "Design & Planning",
    image: "service-design.png",
    imageAlt:
      "Architect reviewing floor plans on paper with a tablet and drafting tools on a desk",
    intro:
      "From concept to permit-ready drawings, with a focus on practical execution and long-term value.",
    items: [
      "3D modelling and visualization",
      "Residential design development",
      "Construction drawing packages",
      "Permit-ready planning",
      "Renovation and redevelopment planning",
    ],
    closing:
      "Planning that considers cost, constructability, and real-world constraints from the start.",
  },
  {
    id: "construction-consulting",
    title: "Construction & Consulting",
    image: "service-construction.jpg",
    imageAlt: "Residential wood framing and roof trusses under construction",
    intro:
      "Execution support, project strategy, and construction expertise to carry projects from idea to completion.",
    items: [
      "General contracting",
      "Renovation consulting",
      "Project management",
      "Construction planning and sequencing",
      "Budget and scope alignment",
    ],
    closing:
      "Clear execution reduces delays, cost overruns, and disconnects between plan and build.",
  },
];

const differentiatorBullets = [
  "One point of accountability across stages",
  "Better decisions earlier in the process",
  "Reduced redesign and rework",
  "Clear path from acquisition to execution",
];

function Services() {
  const pub = process.env.PUBLIC_URL || "";

  return (
    <div className="bg-secondary">
      <header className="border-b border-slate-200/80 bg-white">
        <div className="container mx-auto max-w-4xl px-4 py-10 md:py-14">
          <h1 className="max-w-3xl text-balance text-2xl font-bold leading-[1.22] tracking-tight text-slate-900 md:text-[1.65rem] lg:text-[1.85rem]">
            <span className="block">Residential Real Estate, Design, and Construction</span>
            <span className="mt-1 block text-[1.1em] font-semibold tracking-tight text-slate-800 md:mt-1.5 md:text-[1.05em]">
              — Aligned
            </span>
          </h1>
          <div className="mt-5 h-px w-12 bg-slate-300/90" aria-hidden="true" />
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-slate-600 md:text-[1.125rem] md:leading-relaxed">
            An integrated approach to buying, planning, and building residential property in Calgary.
          </p>
        </div>
      </header>

      {servicePillars.map((pillar, index) => {
        const isMiddle = index === 1;

        return (
          <section
            key={pillar.id}
            id={pillar.id}
            className={
              isMiddle
                ? "border-y border-slate-200/80 bg-white py-10 md:py-12"
                : "border-t border-slate-200/70 bg-secondary py-10 md:py-12"
            }
            aria-labelledby={`${pillar.id}-heading`}
          >
            <div className="container mx-auto max-w-6xl px-4">
              {isMiddle ? (
                <div className="mx-auto max-w-4xl">
                  <div className="flex flex-col gap-7 md:flex-row md:items-start md:gap-9">
                    <div className="relative shrink-0 overflow-hidden rounded-xl border border-slate-200/70 bg-slate-100 shadow-sm md:w-[176px] lg:w-[190px]">
                      <img
                        src={`${pub}/${pillar.image}`}
                        alt={pillar.imageAlt}
                        className="aspect-[4/3] h-full w-full max-h-[160px] object-cover brightness-[0.97] contrast-[1.02] saturate-[0.94] md:max-h-[170px]"
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h2
                        id={`${pillar.id}-heading`}
                        className="text-xl font-bold tracking-tight text-slate-900 md:text-2xl"
                      >
                        {pillar.title}
                      </h2>
                      <p className="mt-3 text-[15px] font-medium leading-relaxed text-slate-700 md:text-base">
                        {pillar.intro}
                      </p>
                      <ul className="mt-5 space-y-2.5 text-[14px] leading-snug text-slate-600 md:text-[15px] md:leading-relaxed">
                        {pillar.items.map((item) => (
                          <li key={item} className="flex gap-2.5">
                            <span
                              className="mt-[0.55em] h-1 w-1 shrink-0 rounded-full bg-slate-400"
                              aria-hidden="true"
                            />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                      <p className="mt-6 rounded-lg border border-slate-200/80 bg-slate-50/90 px-4 py-3.5 text-[15px] font-semibold leading-snug text-slate-800 md:text-[15px]">
                        {pillar.closing}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200/50 md:p-7 lg:p-9">
                  <div className="grid gap-7 lg:grid-cols-12 lg:gap-7 lg:items-start">
                    {index === 2 && (
                      <div className="relative max-h-[200px] overflow-hidden rounded-xl border border-slate-200/70 bg-slate-100 shadow-sm lg:col-span-4">
                        <img
                          src={`${pub}/${pillar.image}`}
                          alt={pillar.imageAlt}
                          className="h-full max-h-[200px] w-full object-cover brightness-[0.97] contrast-[1.02] saturate-[0.94]"
                          loading="lazy"
                          decoding="async"
                        />
                      </div>
                    )}
                    <div
                      className={`min-w-0 lg:col-span-8 ${index === 2 ? "lg:col-start-5" : ""}`}
                    >
                      <h2
                        id={`${pillar.id}-heading`}
                        className="text-xl font-bold tracking-tight text-slate-900 md:text-2xl"
                      >
                        {pillar.title}
                      </h2>
                      <p className="mt-3 text-[15px] font-medium leading-relaxed text-slate-700 md:text-base">
                        {pillar.intro}
                      </p>
                      <ul className="mt-5 max-w-2xl space-y-2.5 text-[14px] leading-snug text-slate-600 md:text-[15px] md:leading-relaxed">
                        {pillar.items.map((item) => (
                          <li key={item} className="flex gap-2.5">
                            <span
                              className="mt-[0.55em] h-1 w-1 shrink-0 rounded-full bg-slate-400"
                              aria-hidden="true"
                            />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                      <p className="mt-6 border-l-[3px] border-slate-400 pl-5 text-[15px] font-semibold leading-snug text-slate-800 md:pl-6">
                        {pillar.closing}
                      </p>
                    </div>
                    {index === 0 && (
                      <div className="relative max-h-[188px] overflow-hidden rounded-xl border border-slate-200/70 bg-slate-100 shadow-sm lg:col-span-4 lg:col-start-9">
                        <img
                          src={`${pub}/${pillar.image}`}
                          alt={pillar.imageAlt}
                          className="h-full max-h-[188px] w-full object-cover brightness-[0.97] contrast-[1.02] saturate-[0.94]"
                          loading="lazy"
                          decoding="async"
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </section>
        );
      })}

      <section
        className="border-t border-slate-200/70 bg-gradient-to-b from-secondary via-white to-white py-8 md:py-10"
        aria-labelledby="differentiator-headline"
      >
        <div className="container mx-auto max-w-6xl px-4">
          <div className="rounded-2xl border border-slate-200/60 bg-white px-5 py-5 shadow-sm md:px-7 md:py-6">
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
              Why This Approach Works
            </p>
            <h2
              id="differentiator-headline"
              className="mt-2 text-xl font-bold tracking-tight text-slate-900 md:text-2xl"
            >
              One Process. Fewer Disconnects.
            </h2>
            <div className="mt-4 max-w-3xl space-y-2.5 text-sm leading-relaxed text-slate-600 md:text-[15px]">
              <p>
                Most residential projects involve separate professionals for real estate, design, and
                construction.
              </p>
              <p>
                That separation often leads to missed opportunities, misaligned decisions, and costly
                changes.
              </p>
              <p>
                This approach keeps everything connected — from the first property decision through
                planning and into construction.
              </p>
            </div>
            <ul className="mt-4 max-w-3xl space-y-2 border-t border-slate-100 pt-4 text-sm leading-relaxed text-slate-600 md:text-[15px]">
              {differentiatorBullets.map((item) => (
                <li key={item} className="flex gap-2.5">
                  <span
                    className="mt-[0.55em] h-1 w-1 shrink-0 rounded-full bg-slate-400"
                    aria-hidden="true"
                  />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="border-t border-slate-200/70 bg-white pb-10 pt-12 md:pb-12 md:pt-14">
        <div className="container mx-auto max-w-3xl px-4 text-center">
          <h2 className="text-xl font-bold tracking-tight text-slate-900 md:text-2xl">
            Start With a Conversation
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-slate-600 md:text-[15px]">
            Whether you are buying, planning, or building, we can help you approach your project with
            clarity from the start.
          </p>
          <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4">
            <Link
              to="/contact"
              className="inline-flex min-h-[42px] w-full max-w-xs items-center justify-center rounded-md border border-slate-400/90 bg-slate-800 px-5 py-2.5 text-sm font-semibold leading-none text-white shadow-sm transition-colors duration-200 hover:border-slate-500 hover:bg-slate-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-400 sm:w-auto"
            >
              Contact
            </Link>
            <Link
              to="/portfolio"
              className="inline-flex min-h-[42px] w-full max-w-xs items-center justify-center rounded-md border border-slate-300 bg-white/95 px-5 py-2.5 text-sm font-semibold leading-none text-slate-800 shadow-sm transition-colors duration-200 hover:border-slate-400 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-400 sm:w-auto"
            >
              View portfolio
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Services;
